import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { ThemeConfig, themeTokensToCssVars } from '@/types/theme';

// ─── Context Types ──────────────────────────────────────────

interface ThemeContextType {
  /** Currently active theme (full config) */
  currentTheme: ThemeConfig | null;
  /** All themes available to this user */
  availableThemes: ThemeConfig[];
  /** Switch to a different theme by ID. Pass 'default' for org default. */
  setTheme: (themeId: string) => void;
  /** Current base theme for conditional logic */
  baseTheme: 'dark' | 'light';
  /** Loading state */
  isLoading: boolean;
  /** Toggle between dark and light by switching to the first published theme with the opposite baseTheme */
  toggleBaseTheme: () => void;
  /** True when at least one dark AND one light published theme exist */
  canToggleBaseTheme: boolean;
}

const ThemeContext = createContext<ThemeContextType>({
  currentTheme: null,
  availableThemes: [],
  setTheme: () => {},
  baseTheme: 'light',
  isLoading: true,
  toggleBaseTheme: () => {},
  canToggleBaseTheme: false,
});

// ─── Constants ──────────────────────────────────────────────

const STORAGE_KEY_THEME_ID = 'app-theme-id';
const STORAGE_KEY_THEME_BASE = 'app-theme';
const STORAGE_KEY_THEME_TOKENS = 'app-theme-tokens';

// ─── Apply CSS Variables ────────────────────────────────────

function applyCssVars(vars: Record<string, string>) {
  const root = document.documentElement;
  Object.entries(vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
}

function clearCustomCssVars() {
  // Remove all inline styles from root (reset to tokens.css defaults)
  document.documentElement.removeAttribute('style');
}

function applyDataTheme(baseTheme: 'dark' | 'light') {
  if (baseTheme === 'light') {
    document.documentElement.setAttribute('data-theme', 'light');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
}

// ─── Provider ───────────────────────────────────────────────

interface ThemeProviderProps {
  orgId: string | null;
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ orgId, children }) => {
  const [availableThemes, setAvailableThemes] = useState<ThemeConfig[]>([]);
  const [currentTheme, setCurrentTheme] = useState<ThemeConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load themes from Firestore for the organization
  useEffect(() => {
    if (!orgId) {
      setIsLoading(false);
      return;
    }

    const loadThemes = async () => {
      try {
        const themesRef = collection(db, 'organizations', orgId, 'themes');
        const snapshot = await getDocs(themesRef);

        const themes: ThemeConfig[] = snapshot.docs.map(d => ({
          ...d.data(),
          id: d.id,
          createdAt: d.data().createdAt?.toDate?.() || new Date(),
          updatedAt: d.data().updatedAt?.toDate?.() || new Date(),
        })) as ThemeConfig[];

        setAvailableThemes(themes);

        // Determine which theme to apply
        const savedThemeId = localStorage.getItem(STORAGE_KEY_THEME_ID);
        let themeToApply: ThemeConfig | undefined;

        // 1. Try user's saved preference
        if (savedThemeId) {
          themeToApply = themes.find(t => t.id === savedThemeId);
        }

        // 2. Fall back to org default
        if (!themeToApply) {
          themeToApply = themes.find(t => t.isDefault);
        }

        // 3. If we have a theme, apply it
        if (themeToApply) {
          applyTheme(themeToApply);
        }
        // If no themes exist, tokens.css defaults are used (Dark Canvas)
      } catch (error) {
        console.error('Failed to load themes:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadThemes();
  }, [orgId]);

  // Apply a theme: set CSS vars + cache
  const applyTheme = useCallback((theme: ThemeConfig) => {
    // Reset to defaults first
    clearCustomCssVars();

    // Set data-theme attribute for light/dark
    applyDataTheme(theme.baseTheme);

    // Apply custom token overrides
    const cssVars = themeTokensToCssVars(theme.tokens);
    applyCssVars(cssVars);

    // Cache for FOUC prevention
    localStorage.setItem(STORAGE_KEY_THEME_ID, theme.id);
    localStorage.setItem(STORAGE_KEY_THEME_BASE, theme.baseTheme);
    localStorage.setItem(STORAGE_KEY_THEME_TOKENS, JSON.stringify(cssVars));

    setCurrentTheme(theme);
  }, []);

  // Public setTheme function
  const setThemeById = useCallback((themeId: string) => {
    if (themeId === 'default') {
      const defaultTheme = availableThemes.find(t => t.isDefault);
      if (defaultTheme) applyTheme(defaultTheme);
      return;
    }

    const theme = availableThemes.find(t => t.id === themeId);
    if (theme) applyTheme(theme);
  }, [availableThemes, applyTheme]);

  const baseTheme = currentTheme?.baseTheme || 'light';

  const publishedThemes = useMemo(
    () => availableThemes.filter(t => t.isPublished || t.isDefault),
    [availableThemes],
  );

  const canToggleBaseTheme = useMemo(
    () => publishedThemes.some(t => t.baseTheme === 'dark') && publishedThemes.some(t => t.baseTheme === 'light'),
    [publishedThemes],
  );

  const toggleBaseTheme = useCallback(() => {
    const targetBase = baseTheme === 'dark' ? 'light' : 'dark';
    const opposite = publishedThemes.find(t => t.baseTheme === targetBase);
    if (opposite) applyTheme(opposite);
  }, [baseTheme, publishedThemes, applyTheme]);

  const contextValue = useMemo(() => ({
    currentTheme,
    availableThemes,
    setTheme: setThemeById,
    baseTheme,
    isLoading,
    toggleBaseTheme,
    canToggleBaseTheme,
  }), [currentTheme, availableThemes, setThemeById, baseTheme, isLoading, toggleBaseTheme, canToggleBaseTheme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// ─── Hook ───────────────────────────────────────────────────

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
